import { useState } from "react";
import { IoIosTrendingUp } from "react-icons/io";

import heroImage from "../../assets/images/hero-illustration.png";
import {
  FaBookOpen,
  FaBrain,
  FaChevronDown,
  FaClock,
  FaGraduationCap,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";
import Button from "../../components/EmptyButton";
import { apiRequest } from "../../helpers";
import { method } from "lodash";
import { useDispatch } from "react-redux";
import { createErrorAlert } from "../../redux/actions";

const PilotOnboarding = () => {
    const dispatch = useDispatch()
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    jobTitle: "",
    email: "",
    institution: "",
    additionalInfo: "",
  });

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        job_title: formData.jobTitle,
        email: formData.email,
        intitution: formData.institution,
        message: formData.additionalInfo,
    }
    await apiRequest({
        method: 'post',
        url: "content/pilot/contact/",
        data: payload,
    }).then((res)=> {
        dispatch(createErrorAlert(200, '', 'Query sent successfully', 200))
    }).catch((e) => {
        dispatch(createErrorAlert(400, '', 'Unable to send query', 400))
    })
  };

  const features = [
    {
      icon: IoIosTrendingUp,
      title: "Reduce Student Dropout",
      description:
        "Recover millions in revenue by keeping students engaged and successful",
    },
    {
      icon: FaBookOpen,
      title: "Seamless LMS Integration",
      description:
        "AI integrates with your existing systems for personalized tutoring",
    },
    {
      icon: FaBrain,
      title: "High-Quality AI Training",
      description: "Trained on academic resources, not random internet content",
    },
    {
      icon: FaShieldAlt,
      title: "Data Ownership & Privacy",
      description:
        "Your institution retains full control and ownership of all data",
    },
    {
      icon: FaClock,
      title: "Save Faculty Time",
      description:
        "Thousands of hours saved by automating routine student questions",
    },
    {
      icon: FaUsers,
      title: "Ethical AI Oversight",
      description: "Full control over AI usage aligned with academic integrity",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <section className="relative overflow-hidden bg-gradient-hero">
        <div className="absolute inset-0 bg-black/5"></div>
        <div className="relative container mx-auto px-4 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left animate-fade-in">
              <h1 className="font-bold text-primary mb-6 leading-tight">
                <span style={{fontSize: '3.75rem', fontWeight: '700'}}>Join Our Pilot Program</span>
              </h1>
              <p
                style={{ fontSize: "24px" }}
                className="text-3xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto text-center"
              >
                We are inviting up to{" "}
                <span className="font-semibold">20 institutions</span> to pilot
                StudyCrowd.AI fully integrated with your LMS. If you are ready
                to transform teaching with an AI tutor and career mentor, apply
                today.
              </p>
              <div className="flex mt-20 flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() => scrollToSection("signup-form")}
                  size="lg"
                  filled={true}
                  className="bg-white text-primary hover:bg-white/90 font-semibold px-8 py-4 text-lg shadow-glow"
                >
                  Apply Now
                </Button>
                <Button
                  onClick={() => scrollToSection("pilot-info")}
                  filled={true}
                  size="lg"
                  className="border-white text-primary bg-white hover:bg-white/90 font-semibold px-8 py-4 text-lg"
                >
                  Learn More
                  <FaChevronDown className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="relative animate-float">
              <img
                src={heroImage}
                alt="University students collaborating with AI technology"
                className="w-3/4 h-auto mx-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        id="pilot-info"
        className="py-20 bg-gradient-to-b from-background to-accent/20"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <h2
              className="text-4xl lg:text-5xl font-bold text-foreground mb-6"
              style={{ fontSize: "25px" }}
            >
              Why a Pilot Program?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Our pilot program lets you try our revolutionary technology that
              helps you transform education and increase student success rates.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group hover:shadow-card transition-all duration-300 hover:-translate-y-2 bg-gradient-card border-0"
              >
                <div className="p-8 text-center">
                  <div className="mb-6 mx-auto w-full h-full bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <feature.icon
                      style={{ width: "60px", height: "60px" }}
                      className="text-primary"
                    />
                  </div>
                  <h3 style={{fontSize: '18px'}} className="text-xl font-bold mb-4 text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-gradient-card border-0 shadow-card">
              <div className="p-8">
                <h3 style={{fontSize: '20px'}} className="text-2xl font-bold mb-6 text-foreground flex items-center">
                  <FaBookOpen className="mr-3 h-6 w-6 text-primary" />
                  Custom Training & Integration
                </h3>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Our AI is trained with high quality academic resources only,
                    unlike other AI tools that are trained with random resources
                    from the internet. Upload your course materials, lectures,
                    and books to train StudyCrowd.AI for your specific needs.
                  </p>
                  <p>
                    Your data will be private and only accessible to your
                    students and staff. We can also integrate your existing
                    tutoring and career advice sessions into the platform for a
                    complete solution.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-card border-0 shadow-card">
              <div className="p-8">
                <h3 style={{fontSize: '20px'}} className="text-2xl font-bold mb-6 text-foreground flex items-center">
                  <FaShieldAlt className="mr-3 h-6 w-6 text-primary" />
                  Complete Data Control
                </h3>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Your institution retains full ownership of all data
                    generated during the pilot. If you choose not to continue
                    with StudyCrowd.AI after the pilot, we can assist with data
                    extraction or delete your data according to your
                    preferences.
                  </p>
                  <p>
                    <em className="font-medium text-foreground">
                      Please note:
                    </em>{" "}
                    There is a modest pilot fee to cover our expert support,
                    integration, and AI server costs.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="inline-block bg-primary/5 border-primary/20">
              <div className="p-6">
                <p className="text-lg mb-4 text-foreground">
                  Want to see our detailed presentation?
                </p>
                <Button
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  asChild
                >
                  <a
                    href="https://gamma.app/docs/AI-TUTOR-AND-CAREER-MENTOR-FOR-COLLEGE-STUDENTS-scy0p7k6lvw5h7e"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Customer Presentation
                    {/* <ExternalLink className="ml-2 h-4 w-4" /> */}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="signup-form" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <h2 className="text-4xl font-bold text-foreground mb-6">
                Apply for the Pilot Program
              </h2>
              <p className="text-xl text-muted-foreground">
                Join the select institutions transforming education with AI
              </p>
            </div>

            <div className="bg-gradient-card border-0 shadow-glow">
              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label
                        htmlFor="firstName"
                        className="text-foreground font-medium"
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        className={`emailTwo block py-2.5 px-0 w-full text-sm `}
                        placeholder="First Name"
                        required
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="lastName"
                        className="text-foreground font-medium"
                      >
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        className={`emailTwo block py-2.5 px-0 w-full text-sm `}
                        placeholder="Last Name"
                        required
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            lastName: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="jobTitle"
                      className="text-foreground font-medium"
                    >
                      Job Title *
                    </label>
                    <input
                      type="text"
                      id="jobTitle"
                      name="jobTitle"
                      className={`emailTwo block py-2.5 px-0 w-full text-sm `}
                      placeholder="Job Title"
                      required
                      value={formData.jobTitle}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          jobTitle: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="text-foreground font-medium"
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className={`emailTwo block py-2.5 px-0 w-full text-sm `}
                      placeholder="Email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          email: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="institution"
                      className="text-foreground font-medium"
                    >
                      Institution or Organization Name *
                    </label>
                    <input
                      type="text"
                      id="institution"
                      name="institution"
                      className={`emailTwo block py-2.5 px-0 w-full text-sm `}
                      placeholder="Institution"
                      required
                      value={formData.institution}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          institution: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="additionalInfo"
                      className="text-foreground font-medium"
                    >
                      Is there anything else you'd like us to know?
                    </label>
                    <textarea
                      rows={8}
                      id="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          additionalInfo: e.target.value,
                        })
                      }
                      className="w-full p-5 my-5 rounded-2xl"
                      placeholder="Tell us about your institution's specific needs or goals..."
                    ></textarea>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-button text-primary-foreground hover:opacity-90 font-semibold py-4 text-lg shadow-glow"
                  >
                    Submit Application
                    <FaGraduationCap className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PilotOnboarding;
